/* import __COLOCATED_TEMPLATE__ from './projects-results-list-item.hbs'; */
import { all } from 'rsvp';
import { A } from '@ember/array';
import { action, computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import Component from '@ember/component';
import { APPLICATION } from "../../models/application";
import { EMBER_ROUTE_NAMES } from "../../constants/routes";
import { showModal } from 'sh-www/utils/modal-controls';
import clsx from 'clsx';

export default Component.extend({
  /**
   * HTML Component properties
   */
  tagName: '',

  shClassNames: computed(
    'class', '_hasMouseEntered', function() {
    return clsx(
      this.class,
      'SH-c-projects-results-list-item',
      'LSG-l-bubble-container',
      'LSG-l-grid',
      this._hasMouseEntered && 'has-mouse'
    );
  }),

  /**
   * Register services
   */
  applicationStateManager: service('state-managers/application'),
  assignmentCommandService: service('commands/assignment-commands'),
  session: service(),
  store: service(),
  state: service('states/routes/projects'),
  stateManager: service('state-managers/routes/projects'),
  router: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * The application model for this component passed in from caller.
   *
   * @param {ApplicationSummary|null}
   */
  application: null,

  currentUserCompanyTeams: [],

  updatedCompanyTeamAssociation: null,

  /**
   * @inheritDoc
   */
  onMouseEnter: action(function() {
    this.set('_hasMouseEntered', true);
  }),

  /**
   * @inheritDoc
   */
  onMouseLeave: action(function() {
    this.set('_hasMouseEntered', false);
  }),

  /**
   * True when the mouse has entered our element.
   *
   * @type {Boolean}
   * @private
   */
  _hasMouseEntered: false,

  /**
   * Will be set to true when the action button is being hovered.
   *
   * @type {Boolean}
   * @private
   */
  _isActionButtonHovered: false,

  /**
   * True when the application title should be underlined.
   *
   * @type {Boolean}
   * @private
   */
  _isApplicationUnderlined: computed('_isActionButtonHovered', '_hasMouseEntered', function() {
    return !this.get('_isActionButtonHovered') && this.get('_hasMouseEntered');
  }),

  _companyTeamAssociation: computed(
    'updatedCompanyTeamAssociation',
    'application.companyTeam',
    function() {
      return this.updatedCompanyTeamAssociation ?? this.application.companyTeam ?? null;
    }
  ),

  _availableTeamDropdownOptions: computed(
    'currentUserCompanyTeams',
    'application.assignmentId',
    function() {
      return this.currentUserCompanyTeams.reduce((accum, companyTeam) => {
        accum.push({
          title: companyTeam.name,
          onClick: () => this.saveCompanyTeamAssociation.perform(this.application.assignmentId, companyTeam),
        });
        return accum;
      }, []);
    }
  ),

  /**
   * The array of menu dropdown options in the format:
   *
   * [{
   *    title: 'Edit',
   *    onClick: Function   // callback function for the click on this menu item
   *    hasSeparatorAfter: Boolean
   *  },{...}]
   *
   * @type {Ember.MutableArray}
   */
  _menuDropdownOptions: computed(
  'application.status',
  function() {
    let menuOptions     = A(),
      applicationStatus = this.get('application.status'),
      addSendMessage    = (hasSeparatorAfter) => {
        menuOptions.pushObject({
          title: 'Send message',
          onClick: () => this._onSendMessageClicked(),
          hasSeparatorAfter: hasSeparatorAfter
        });
      };

    if (applicationStatus === APPLICATION.STATUS.ACTIVE) {
      addSendMessage(true);

      menuOptions.pushObject({
        title: 'Mark complete',
        onClick: () => this._onMarkCompleteClicked()
      });
    } else if (applicationStatus === APPLICATION.STATUS.CANCEL_PENDING) {
      addSendMessage(true);

      menuOptions.pushObject({
        title: 'Undo cancellation',
        onClick: () => this._onUndoCancellationClicked()
      });
    } else if (applicationStatus === APPLICATION.STATUS.COMPLETE_PRODUCER) {
      addSendMessage(true);

      menuOptions.pushObject({
        title: 'Confirm completion',
        onClick: () => this._onMarkCompleteClicked()
      });
    } else {
      addSendMessage(false);
    }

    return menuOptions;
  }),

  saveCompanyTeamAssociation: task(function *(assignmentId, companyTeam) {
    try {
      yield this.assignmentCommandService.setCompanyTeam(assignmentId, companyTeam.id);
      yield this.assignmentCommandService.loadAssignment(assignmentId, ['company-team']);
      this.set('updatedCompanyTeamAssociation', companyTeam);
    } catch(e) {
      this.applicationStateManager.addToast('Cannot update this project\'s team');
    }
  }).drop(),

  _onMarkCompleteClicked: action(function() {
    this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__COMPLETE, this.application.id);
  }),

  _onSendMessageClicked: action(function() {
    const company = this.store.peekRecord('company', this.currentUser.company.id);

    if (company.isUserBlockedByCompany(this.application.producerId)) {
      const freelancerIsBlockedModalModel = {
        freelancerUserName: this.application.producerName,
        freelancerSlug: this.application.producerSlug
      };

      showModal(this, 'freelancer-is-blocked', freelancerIsBlockedModalModel);
      return;
    }

    // Attempt to find user and thread in the store.
    let user = this.store.peekRecord('user', this.application.producerId);
    let thread = this.store.peekRecord('thread', this.application.threadId);

    // Could not find user in store, resort to network request.
    if (!user) {
      user = this.store.findRecord('user', this.application.producerId);
    }

    // Could not find thread in store, resort to network request.
    if (!thread) {
      thread = this.store.findRecord('thread', this.application.threadId);
    }

    // Once user and thread have been found, show the modal.
    all([user, thread])
      .then(() => {
        showModal(this, 'lsg/lsg-send-message', {
          userToSendMessageTo: user,
          thread,
        });
      })
      .catch(() => {
        showModal(this, 'confirmation', {
          width: 'medium',
          titleText: 'Failed to get user information',
          bodyText: 'Please try again later.',
          actionButtonText: 'Ok',
        });
      });
  }),

  _onUndoCancellationClicked: action(function() {
    const data = {
      titleText: "Undo cancellation",
      bodyText: `This project will become active again and ${this.application.producerName} will be notified.`,
      actionButtonText: 'Undo cancellation',
      cancelButtonText: 'Cancel',
      confirmAction: () => {
        this.stateManager.undoCancellation(this.application.id);
      }
    };

    showModal(this, 'confirmation', data);
  }),
});
